import React, { useEffect, useState } from 'react';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Legend
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format, subDays, subHours, parseISO } from 'date-fns';
import { useDeviceChartData } from '@/hooks/useDeviceChartData';
import { formatDeviceId } from '@/utils/deviceUtils';
import { fetchTriggerRange } from '@/utils/fetchTriggers';


interface MultiDeviceChartProps {
  devices: Array<{
    device_id: string;
    name?: string;
    hardware_type?: string;
  }>;
  chartType: 'line' | 'stackedBar';
  days?: number;
  title: string;
}

// Expanded color palette with more distinct colors
const DEVICE_COLORS = [
  '#4f46e5', // Indigo
  '#ef4444', // Red
  '#10b981', // Emerald
  '#f59e0b', // Amber
  '#8b5cf6', // Violet
  '#06b6d4', // Cyan
  '#84cc16', // Lime
  '#f97316', // Orange
  '#ec4899', // Pink
  '#6366f1', // Indigo (lighter)
  '#dc2626', // Red (darker)
  '#059669', // Emerald (darker)
  '#d97706', // Amber (darker)
  '#7c3aed', // Violet (darker)
  '#0891b2', // Cyan (darker)
  '#65a30d', // Lime (darker)
  '#ea580c', // Orange (darker)
  '#db2777', // Pink (darker)
  '#3730a3', // Indigo (darkest)
  '#991b1b'  // Red (darkest)
];

// Function to get a consistent color for a device based on its ID
const getDeviceColor = (deviceId: string, index: number) => {
  // Use device ID hash for consistent color assignment across renders
  const hash = deviceId.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  const colorIndex = Math.abs(hash) % DEVICE_COLORS.length;
  
  // Fallback to index-based color if hash gives same result
  return DEVICE_COLORS[colorIndex] || DEVICE_COLORS[index % DEVICE_COLORS.length];
};

const MultiDeviceChart: React.FC<MultiDeviceChartProps> = ({ 
  devices,
  chartType,
  days = 7,
  title
}) => {
  const [chartData, setChartData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { toast } = useToast();

  // Use the real-time hook for the first device to trigger updates
  const { lastUpdate } = useDeviceChartData({
    deviceId: devices[0]?.device_id || '',
    enabled: devices.length > 0
  });

  const processMultiDeviceData = (events: any[]) => {
    const data: Record<string, any> = {};
    const now = new Date();
    
    if (days === 0) {
      // Hourly data for last 24 hours
      for (let h = 0; h < 24; h++) {
        const hour = subHours(now, 23 - h);
        const hourKey = format(hour, 'yyyy-MM-dd-HH');
        data[hourKey] = {
          date: hourKey,
          displayDate: format(hour, 'HH:mm'),
          total: 0
        };
        
        // Initialize each device count to 0
        devices.forEach(device => {
          const deviceKey = device.name || formatDeviceId(device.device_id, true);
          data[hourKey][deviceKey] = 0;
        });
      }

      // Process events for each device (hourly)
      devices.forEach(device => {
        const deviceEvents = events.filter(event => event.device_id === device.device_id);
        const deviceKey = device.name || formatDeviceId(device.device_id, true);
        
        deviceEvents.forEach(event => {
          const eventTime = new Date(event.created_at);
          const hourKey = format(eventTime, 'yyyy-MM-dd-HH');
          
          if (data[hourKey]) {
            data[hourKey][deviceKey] += 1;
            data[hourKey].total += 1;
          }
        });
      });
    } else {
      // Daily data
      for (let d = 0; d < days; d++) {
        const date = subDays(now, days - 1 - d);
        const formattedDate = format(date, 'yyyy-MM-dd');
        data[formattedDate] = {
          date: formattedDate,
          displayDate: format(date, 'MMM dd'),
          total: 0
        };
        
        // Initialize each device count to 0
        devices.forEach(device => {
          const deviceKey = device.name || formatDeviceId(device.device_id, true);
          data[formattedDate][deviceKey] = 0;
        });
      }

      // Process events for each device (daily)
      devices.forEach(device => {
        const deviceEvents = events.filter(event => event.device_id === device.device_id);
        const deviceKey = device.name || formatDeviceId(device.device_id, true);
        
        deviceEvents.forEach(event => {
          const eventTime = new Date(event.created_at);
          const formattedDate = format(eventTime, 'yyyy-MM-dd');
          
          if (data[formattedDate]) {
            data[formattedDate][deviceKey] += 1;
            data[formattedDate].total += 1;
          }
        });
      });
    }

    return Object.values(data).sort((a: any, b: any) => a.date.localeCompare(b.date));
  };

  const fetchMultiDeviceData = async () => {
    try {
      setIsLoading(true);
      
      const now = new Date();
      const startDate = days === 0 ? subHours(now, 24) : subDays(now, days);
      const deviceIds = devices.map(d => d.device_id);
      
      if (deviceIds.length === 0) {
        setChartData([]);
        return;
      }
      
      let motionEvents: any[] = [];
      try {
        motionEvents = await fetchTriggerRange({
          deviceIds,
          start: startDate.toISOString(),
          end: now.toISOString(),
        });
      } catch (error) {
        console.error('Error fetching multi-device data:', error);
        toast({
          title: "Data Fetch Error",
          description: "Failed to fetch multi-device motion data",
          variant: "destructive",
        });
        return;
      }

      const processedData = processMultiDeviceData(motionEvents || []);

      setChartData(processedData);
      
    } catch (error: any) {
      console.error('Unexpected error:', error);
      toast({
        title: "Data Fetch Error",
        description: "Failed to fetch multi-device motion data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (devices.length > 0) {
      fetchMultiDeviceData();
    }
  }, [devices, days, lastUpdate]);

  const renderChart = () => {
    if (chartType === 'line') {
      return (
        <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis dataKey="displayDate" />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Legend />
          {devices.map((device, index) => {
            const deviceKey = device.name || formatDeviceId(device.device_id, true);
            const deviceColor = getDeviceColor(device.device_id, index);
            return (
              <Line
                key={device.device_id}
                type="monotone"
                dataKey={deviceKey}
                stroke={deviceColor}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            );
          })}
        </LineChart>
      );
    } else {
      return (
        <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis dataKey="displayDate" />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Legend />
          {devices.map((device, index) => {
            const deviceKey = device.name || formatDeviceId(device.device_id, true);
            const deviceColor = getDeviceColor(device.device_id, index);
            return (
              <Bar
                key={device.device_id}
                dataKey={deviceKey}
                stackId="activity"
                fill={deviceColor}
              />
            );
          })}
        </BarChart>
      );
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="py-3">
        <CardTitle className="text-md">{title}</CardTitle>
        <CardDescription>
          Motion activity across all master devices over the last {days === 0 ? '24 hours' : `${days} days`}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-1 pt-0">
        {isLoading ? (
          <div className="flex justify-center items-center h-[300px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            {renderChart()}
          </ResponsiveContainer>
        ) : (
          <div className="flex flex-col justify-center items-center h-[300px] text-muted-foreground">
            <div>No motion data available for the selected devices</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MultiDeviceChart;
